import React, { useId } from 'react';
import roddoFishPng from '../assets/roddo-fish.png';

type IconProps = {
  className?: string;
  style?: React.CSSProperties;
};

/* ---------------------------------------------------------------------------
 * Icon style constants (Roddo: chunky + clean)
 * -------------------------------------------------------------------------*/
const ICON_STROKE = 2.5;
const ICON_STROKE_BOLD = 3;

/* ---------------------------------------------------------------------------
 * Helper: "Bubble Shine" (kept as-is)
 * -------------------------------------------------------------------------*/
const BubbleShine = () => (
  <circle cx="18" cy="6" r="3" fill="white" fillOpacity="0.25" />
);

/* ---------------------------------------------------------------------------
 * Section Header Signs (Wooden boards)
 * -------------------------------------------------------------------------*/

type SectionSignProps = IconProps & {
  title: string;
  subtitle?: string;
  titleSize?: number;
  subtitleSize?: number;
  maxTitleWidth?: number;
};

const WoodFilters: React.FC<{ uid: string }> = ({ uid }) => (
  <defs>
    <filter id={`burned-sec-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="0.4" result="blur" />
      <feColorMatrix
        in="blur"
        type="matrix"
        values="0 0 0 0 0.2  0 0 0 0 0.1  0 0 0 0 0.05  0 0 0 1 0"
        result="char"
      />
      <feOffset in="SourceAlpha" dx="0" dy="1" result="highlightOffset" />
      <feComposite in="highlightOffset" in2="SourceAlpha" operator="out" result="highlightMask" />
      <feFlood floodColor="#ffffff" floodOpacity="0.2" result="white" />
      <feComposite in="white" in2="highlightMask" operator="in" result="highlight" />
      <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" result="blur2" />
      <feOffset in="blur2" dx="0" dy="1" result="offsetBlur" />
      <feComposite
        in="offsetBlur"
        in2="SourceAlpha"
        operator="arithmetic"
        k2="-1"
        k3="1"
        result="innerShadowMask"
      />
      <feFlood floodColor="#000000" floodOpacity="0.7" result="black" />
      <feComposite in="black" in2="innerShadowMask" operator="in" result="innerShadow" />
      <feMerge>
        <feMergeNode in="char" />
        <feMergeNode in="highlight" />
        <feMergeNode in="SourceGraphic" />
        <feMergeNode in="innerShadow" />
      </feMerge>
    </filter>
  </defs>
);

const SectionSign: React.FC<SectionSignProps> = ({
  className,
  title,
  subtitle,
  titleSize = 30,
  subtitleSize = 11,
  maxTitleWidth,
}) => {
  const uid = React.useId();
  const shouldCompress = typeof maxTitleWidth === 'number' && title.length >= 12;

  return (
    <svg className={className} viewBox="0 0 180 70" xmlns="http://www.w3.org/2000/svg">
      <WoodFilters uid={uid} />
      <path
        d="M10 12 L 25 10 L 55 13 L 90 11 L 125 14 L 155 12 L 170 14 L 175 22 L 172 32 L 178 42 L 170 52 L 173 56 L 155 58 L 120 56 L 85 59 L 50 57 L 25 58 L 10 56 L 4 45 L 8 35 L 3 25 L 10 15 Z"
        fill="#d4a373"
        stroke="#6d4c41"
        strokeWidth="1.5"
      />
      <path
        d="M10 15 L 25 18 M10 52 L 28 50 M170 18 L 155 22 M170 50 L 150 48"
        fill="none"
        stroke="#5d4037"
        strokeWidth="0.8"
        opacity="0.4"
      />
      <path
        d="M4 25 Q 12 35, 4 45 L 15 35 Z"
        fill="#2e1a12"
        opacity="0.15"
        filter={`url(#burned-sec-${uid})`}
      />
      <path
        d="M176 25 Q 168 35, 176 45 L 165 35 Z"
        fill="#2e1a12"
        opacity="0.15"
        filter={`url(#burned-sec-${uid})`}
      />
      <path
        d="M15 20 Q 50 18, 90 20 T 165 19"
        fill="none"
        stroke="#a17a5b"
        strokeWidth="1"
        opacity="0.6"
      />
      <path
        d="M15 40 Q 50 42, 90 40 T 165 41"
        fill="none"
        stroke="#a17a5b"
        strokeWidth="1"
        opacity="0.6"
      />
      <text
        x="90"
        y="36"
        fontFamily="'Gaegu', cursive"
        fontWeight="700"
        fontSize={titleSize}
        fill="#2e1a12"
        textAnchor="middle"
        filter={`url(#burned-sec-${uid})`}
        {...(shouldCompress
          ? { textLength: maxTitleWidth, lengthAdjust: 'spacingAndGlyphs' as const }
          : {})}
      >
        {title}
      </text>
      {subtitle && (
        <text
          x="90"
          y="52"
          fontFamily="'Gaegu', cursive"
          fontWeight="700"
          fontSize={subtitleSize}
          fill="#2e1a12"
          textAnchor="middle"
          filter={`url(#burned-sec-${uid})`}
          opacity="0.9"
        >
          {subtitle}
        </text>
      )}
      <circle cx="14" cy="18" r="1.5" fill="#90a4ae" stroke="#546e7a" strokeWidth="0.5" />
      <circle cx="166" cy="18" r="1.5" fill="#90a4ae" stroke="#546e7a" strokeWidth="0.5" />
    </svg>
  );
};

export const TheHoldHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="The Hold" subtitle="GEAR & STORAGE" />
);
export const RoddoLogHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Roddo Log" subtitle="CATCH HISTORY" />
);
export const BigBlueHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Big Blue" subtitle="GLOBAL CHART" titleSize={30} subtitleSize={11} />
);
export const BaleenBayHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Baleen Bay" subtitle="SOCIAL HUB" />
);
export const CrewDeckHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Crew Deck" subtitle="PRIVATE BOARDS" />
);
export const BazaarHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Baleen Bay Bazaar" subtitle="TRADING POST" titleSize={24} maxTitleWidth={150} />
);
export const AchievementsHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Achievements" subtitle="PROGRESS & BADGES" />
);
export const ChallengesHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Challenges" subtitle="RANKS & XP" />
);
export const TournamentsHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign
    className={className}
    title="Tournaments"
    subtitle="COMPETE & EXPLORE"
    titleSize={25}
    maxTitleWidth={150}
  />
);
export const ProfileHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Profile" subtitle="SETTINGS" />
);
export const SupportHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Support" subtitle="SETTINGS" />
);
export const LegalHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Legal" subtitle="SETTINGS" />
);
export const TermsHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Terms" subtitle="SETTINGS" />
);
export const PrivacyHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Privacy" subtitle="SETTINGS" />
);
export const NotificationsHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Notifications" subtitle="SETTINGS" titleSize={24} maxTitleWidth={148} />
);
export const DevicesHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Devices" subtitle="SETTINGS" />
);
export const FishIQHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="FishIQ" subtitle="POCKET FISHING BUDDY" />
);
export const BookIcon: React.FC<IconProps> = ({ className = 'w-6 h-6' }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label="FishIQ"
  >
    <path d="M7.5 4.5h9a2 2 0 0 1 2 2v13a1.5 1.5 0 0 0-1.5-1.5h-9a2.5 2.5 0 0 0-2.5 2.5V7a2.5 2.5 0 0 1 2.5-2.5Z" />
    <path d="M6 6.6v13.9" />
    <path d="M8.6 7.4h7.6" />
  </svg>
);
export const RoddoSecondaryHeader: React.FC<IconProps> = ({ className }) => (
  <SectionSign className={className} title="Roddo" subtitle="POCKET FISHING BUDDY" />
);
export const RoddoFishIcon: React.FC<
  IconProps & {
    variant?: 'blue' | 'mint' | 'peach' | 'violet';
    animated?: boolean;
    mode?: 'svg' | 'png';
  }
> = ({ className = 'h-9 w-9', style, variant = 'blue', animated = true, mode = 'png' }) => {
  const gid = useId();

  if (mode === 'png') {
    return (
      <img
        src={roddoFishPng}
        className={className}
        alt="Roddo"
        draggable={false}
        style={{ display: 'block', ...style }}
      />
    );
  }

  const stops =
    variant === 'mint'
      ? { a: '#A7F3D0', b: '#60A5FA' }
      : variant === 'peach'
        ? { a: '#FDBA74', b: '#93C5FD' }
        : variant === 'violet'
          ? { a: '#C4B5FD', b: '#93C5FD' }
          : { a: '#93C5FD', b: '#2563EB' };

  return (
    <svg viewBox="0 0 256 256" className={className} style={style} role="img" aria-label="Roddo" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={`${gid}-rod-fish`} x1="40" y1="40" x2="216" y2="216" gradientUnits="userSpaceOnUse">
          <stop stopColor={stops.a} offset="0" />
          <stop stopColor={stops.b} offset="1" />
        </linearGradient>
      </defs>
      <g>
        {animated && (
          <animateTransform attributeName="transform" type="translate" values="0 -1; 0 2; 0 -1" dur="2.8s" repeatCount="indefinite" />
        )}
        <path
          d="M56 140 C56 108, 92 88, 128 84 C152 68, 182 72, 204 88 C222 90, 240 102, 248 122 C240 144, 222 156, 204 158 C182 174, 152 178, 128 162 C92 158, 56 172, 56 140 Z
             M56 140 C44 128, 28 116, 10 112 C22 134, 22 146, 10 168 C28 164, 44 152, 56 140 Z
             M118 96 C112 108, 110 118, 116 130 C124 124, 134 118, 146 116 C134 108, 126 102, 118 96 Z"
          fill={`url(#${gid}-rod-fish)`}
        />
      </g>
    </svg>
  );
};

type SvgBaseProps = IconProps & {
  viewBox?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number | string;
  strokeLinecap?: 'round' | 'butt' | 'square' | 'inherit';
  strokeLinejoin?: 'round' | 'miter' | 'bevel' | 'inherit';
  children: React.ReactNode;
};

const SvgBase: React.FC<SvgBaseProps> = ({
  className,
  viewBox = '0 0 24 24',
  fill = 'none',
  stroke = 'currentColor',
  strokeWidth = ICON_STROKE,
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  children,
}) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox={viewBox}
    fill={fill}
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinecap={strokeLinecap}
    strokeLinejoin={strokeLinejoin}
  >
    {children}
  </svg>
);

const SolidSvg: React.FC<IconProps & { viewBox?: string; strokeWidth?: number | string; children: React.ReactNode }> = ({
  className,
  viewBox = '0 0 24 24',
  strokeWidth = 1.5,
  children,
}) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox={viewBox}
    fill="currentColor"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

export const HomeIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path d="M11.47 3.84a.75.75 0 011.06 0l8.632 8.632a.75.75 0 011.097 1.06L21 14.815V20.25A2.25 2.25 0 0118.75 22.5H5.25A2.25 2.25 0 013 20.25v-5.435l-1.259-1.259a.75.75 0 011.097-1.06l8.632-8.632z" stroke="none" />
    <BubbleShine />
  </SolidSvg>
);

export const IdCardIcon: React.FC<IconProps> = ({ className = '' }) => (
  <SvgBase className={className} strokeWidth={1.75} fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M7 15c1.5-1 4.5-1 6 0" />
    <line x1="14" y1="10" x2="18" y2="10" />
    <line x1="14" y1="14" x2="18" y2="14" />
  </SvgBase>
);

export const MapIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path
      fillRule="evenodd"
      d="M8.161 2.58a1.875 1.875 0 011.678 0l4.993 2.498c.106.052.23.052.336 0l3.869-1.935A1.875 1.875 0 0121.75 4.82v12.485c0 .71-.401 1.36-1.037 1.677l-4.875 2.437a1.875 1.875 0 01-1.676 0l-4.994-2.497a.375.375 0 00-.336 0l-3.868 1.935A1.875 1.875 0 012.25 19.18V6.695c0-.71.401-1.36 1.036-1.677l4.875-2.437zM9 6a.75.75 0 01.75.75V15a.75.75 0 01-1.5 0V6.75A.75.75 0 019 6zm6.75 3a.75.75 0 00-1.5 0v8.25a.75.75 0 001.5 0V9z"
      clipRule="evenodd"
      stroke="none"
    />
    <BubbleShine />
  </SolidSvg>
);

/* ---------------------------------------------------------------------------
 * Everything below here is intentionally unchanged from your current file,
 * except SpinnerIcon now uses currentColor so it won’t force a blue island.
 * -------------------------------------------------------------------------*/
export const DevicesIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path
      d="M5.566 4.657A4.505 4.505 0 016.75 4.5h10.5c.41 0 .806.055 1.183.157A3 3 0 0015.75 3h-7.5a3 3 0 00-2.684 1.657zM2.25 12a3 3 0 013-3h13.5a3 3 0 013 3v6a3 3 0 01-3 3H5.25a3 3 0 01-3-3v-6zM5.25 7.5c-.41 0-.806.055-1.184.157A3 3 0 016.75 6h10.5a3 3 0 012.683 1.657A4.505 4.505 0 0018.75 7.5H5.25z"
      stroke="none"
    />
    <BubbleShine />
  </SolidSvg>
);

export const TrendsIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M2.25 13.5a8.25 8.25 0 018.25-8.25.75.75 0 01.75.75v6.75H18a.75.75 0 01.75.75 8.25 8.25 0 01-16.5 0zm1.5 0h11.25a6.75 6.75 0 00-11.25 0zm12.75 0a6.75 6.75 0 01-6.75 6.75v-6.75h6.75z"
      clipRule="evenodd"
    />
    <path d="M19.354 5.354a.75.75 0 010 1.06l-2.5 2.5a.75.75 0 11-1.06-1.06l1.22-1.22H15a.75.75 0 010-1.5h3.5a.75.75 0 01.75.75v3.5a.75.75 0 11-1.5 0v-1.22l-1.22 1.22a.75.75 0 11-1.06-1.06l2.5-2.5z" />
  </svg>
);

export const CommunityIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path
      d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643v-.212a9.375 9.375 0 00-5.246-8.243 4.87 4.87 0 01-.004 9.006z"
      stroke="none"
    />
    <BubbleShine />
  </SolidSvg>
);

// ---- from here onward, keep the rest of your original file unchanged ----
// Paste EVERYTHING below this comment from your current icons file,
// starting at `export const LicenseIcon...` all the way to the end.

export const LicenseIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.5 3.75a3 3 0 00-3 3v10.5a3 3 0 003 3h15a3 3 0 003-3V6.75a3 3 0 00-3-3h-15zM7.5 7.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-1.875 6a3.375 3.375 0 016.75 0v.75a.75.75 0 01-.75.75h-5.25a.75.75 0 01-.75-.75v-.75zm9.375-3.375h3.75a.75.75 0 010 1.5h-3.75a.75.75 0 010-1.5zm0 3.75h3.75a.75.75 0 010 1.5h-3.75a.75.75 0 010-1.5zm0 3.75h3.75a.75.75 0 010 1.5h-3.75a.75.75 0 010-1.5z" />
  </svg>
);

export const ProfileIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
      clipRule="evenodd"
    />
  </svg>
);

export const UserIcon: React.FC<IconProps> = ProfileIcon;

export const MoonIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.7-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
      clipRule="evenodd"
    />
  </svg>
);

export const SunIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
  </svg>
);

export const CogIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z"
      clipRule="evenodd"
    />
  </svg>
);

export const CloudIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.5 9.75a6 6 0 0111.573-2.226 3.75 3.75 0 014.133 4.303A4.5 4.5 0 0118 20.25H6.75a5.25 5.25 0 01-2.25-10.5z"
      clipRule="evenodd"
    />
  </svg>
);

export const RainIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.25a5.25 5.25 0 00-5.25 5.25v.75a.75.75 0 01-1.5 0V7.5a6.75 6.75 0 01113.5 0v.75a.75.75 0 01-1.5 0V7.5A5.25 5.25 0 0012 2.25z" />
    <path d="M6.375 11.25a.75.75 0 01.75.75v6a.75.75 0 01-1.5 0v-6a.75.75 0 01.375-.75zM12 11.25a.75.75 0 01.75.75v9a.75.75 0 01-1.5 0v-9a.75.75 0 01.75-.75zM17.625 11.25a.75.75 0 01.75.75v6a.75.75 0 01-1.5 0v-6a.75.75 0 01.75-.75z" />
  </svg>
);

export const DropletIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-.53 14.03a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V8.25a.75.75 0 00-1.5 0v5.69l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3z"
      clipRule="evenodd"
    />
  </svg>
);

export const WindIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 12.75a2.25 2.25 0 10-1.282-4.136.75.75 0 01-.617-1.365A3.75 3.75 0 1118 14.25h-5.25a.75.75 0 010-1.5H18zM15 17.25a2.25 2.25 0 10-1.282-4.136.75.75 0 01-.617-1.365A3.75 3.75 0 1115 18.75h-2.25a.75.75 0 010-1.5H15zM2.25 10.5a.75.75 0 01.75-.75h2.25a.75.75 0 010 1.5H3a.75.75 0 01-.75-.75zM2.25 15a.75.75 0 01.75-.75h5.25a.75.75 0 010 1.5H3a.75.75 0 01-.75-.75z" />
  </svg>
);

export const BellIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path
      fillRule="evenodd"
      d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.244.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9zm4.502 8.9a2.25 2.25 0 104.496 0 25.057 25.057 0 01-4.496 0z"
      clipRule="evenodd"
      stroke="none"
    />
    <BubbleShine />
  </SolidSvg>
);

/* ---------------------------------------------------------------------------
 * Everything below here kept exactly as you pasted (unchanged)
 * -------------------------------------------------------------------------*/

/* ... keep the rest of your file exactly the same from SearchIcon onward ... */

export const SearchIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
    />
  </SvgBase>
);

export const CameraIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.75}>
    <path
      fillRule="evenodd"
      d="M8.25 5.25c.36 0 .695-.193.878-.507l.373-.64A2.25 2.25 0 0111.444 3h1.112c.803 0 1.547.428 1.943 1.103l.373.64c.183.314.518.507.878.507h1.5A2.25 2.25 0 0119.5 7.5v9A2.25 2.25 0 0117.25 18.75H6.75A2.25 2.25 0 014.5 16.5v-9a2.25 2.25 0 012.25-2.25h1.5zM12 8.25a4.5 4.5 0 100 9 4.5 4.5 0 000-9z"
      clipRule="evenodd"
      stroke="none"
    />
    <path d="M12 10.125a2.625 2.625 0 100 5.25 2.625 2.625 0 000-5.25z" stroke="none" />
    <path d="M16.875 8.625h.01" />
    <BubbleShine />
  </SolidSvg>
);

export const MoreHorizontalIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h.01M12 12h.01M19 12h.01" />
  </SvgBase>
);

export const EditIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z" />
  </svg>
);

export const TrashIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z"
      clipRule="evenodd"
    />
  </svg>
);

export const MessageSquareIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.804 21.644A6.707 6.707 0 006 21.75a6.721 6.721 0 003.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 01-.814 1.686.75.75 0 00.44 1.223zM8.25 10.875a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zM10.875 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z"
      clipRule="evenodd"
    />
  </svg>
);

// Receipt / Invoice icon
export const ReceiptIcon: React.FC<IconProps> = ({ className = '' }) => (
  <SvgBase className={className} fill="none" strokeWidth={2}>
    <path d="M4 2h16v20l-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1z" />
    <path d="M8 7h8" />
    <path d="M8 11h8" />
    <path d="M8 15h6" />
  </SvgBase>
);

export const PrinterIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="currentColor" stroke="currentColor" strokeWidth={1.5}>
    <path d="M6 9V2h12v7" fill="none" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" fill="none" />
    <path d="M6 14h12v8H6z" fill="none" />
  </SvgBase>
);

export const CopyIcon: React.FC<IconProps> = ({ className = '' }) => (
  <SvgBase className={className} viewBox="0 0 24 24" fill="none" strokeWidth={2}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </SvgBase>
);

export const ArrowLeftIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </SvgBase>
);

export const SendIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
  </svg>
);

export const ShieldIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.352-.272-2.636-.759-3.807a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.255zm.93 9.153l4.25-4.25a.75.75 0 00-1.06-1.06l-3.72 3.72-1.47-1.47a.75.75 0 10-1.06 1.06l2 2a.75.75 0 001.06 0z"
      clipRule="evenodd"
    />
  </svg>
);

export const PlusCircleIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z"
      clipRule="evenodd"
    />
  </svg>
);

export const PlusIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z"
      clipRule="evenodd"
    />
  </svg>
);

export const StarIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
      clipRule="evenodd"
    />
  </svg>
);

export const SonarIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
    <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 .183.027.363.08.538l5.75 4.028a.75.75 0 00.9.005l4.591-3.612a4.35 4.35 0 001.846-2.929 1.5 1.5 0 00.053-.525 3.19 3.19 0 00-2.022-2.432 6.13 6.13 0 00-4.448-2.459z" />
  </svg>
);

export const BriefcaseIcon: React.FC<IconProps> = ({ className }) => (
  <SolidSvg className={className} strokeWidth={1.5}>
    <path
      fillRule="evenodd"
      d="M9 4.5A2.25 2.25 0 0111.25 2.25h1.5A2.25 2.25 0 0115 4.5V6h2.25A2.25 2.25 0 0119.5 8.25v1.125a.75.75 0 01-.55.723 25.61 25.61 0 01-13.9 0A.75.75 0 014.5 9.375V8.25A2.25 2.25 0 016.75 6H9V4.5zm1.5 1.5h3V4.5a.75.75 0 00-.75-.75h-1.5a.75.75 0 00-.75.75V6z"
      clipRule="evenodd"
      stroke="none"
    />
    <path
      d="M4.5 11.083a27.111 27.111 0 0015 0V15.75A2.25 2.25 0 0117.25 18H6.75A2.25 2.25 0 014.5 15.75v-4.667z"
      stroke="none"
    />
    <path d="M10.5 10.5v1.125c0 .207.168.375.375.375h2.25a.375.375 0 00.375-.375V10.5" fill="none" />
    <BubbleShine />
  </SolidSvg>
);

/* ---------------------------------------------------------------------------
 * Progress + Competition Icons (Tournaments / Challenges / Achievements)
 * -------------------------------------------------------------------------*/

export const TrophyIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
    <path d="M9 20h6" />
    <path d="M10 17h4" />
    <path d="M12 13v4" />
    <path d="M6 6h2v1a3 3 0 0 1-3 3H4V8a2 2 0 0 1 2-2z" />
    <path d="M18 6h-2v1a3 3 0 0 0 3 3h1V8a2 2 0 0 0-2-2z" />
  </SvgBase>
);

export const MedalIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M9 2l3 5 3-5" />
    <path d="M9 2h6" />
    <circle cx="12" cy="14" r="5" />
    <path d="M12 11v6" />
    <path d="M9.5 14H14.5" />
  </SvgBase>
);

export const LaurelIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M8 19c-2.5-1.2-4-3.7-4-6.3C4 8.3 6.2 5.4 9.5 4.3" />
    <path d="M16 19c2.5-1.2 4-3.7 4-6.3 0-4.4-2.2-7.3-5.5-8.4" />
    <path d="M8.2 13.2l-2.1-.7" />
    <path d="M8.8 10.6l-2.3.2" />
    <path d="M9.8 8.2l-1.8 1.4" />
    <path d="M15.8 13.2l2.1-.7" />
    <path d="M15.2 10.6l2.3.2" />
    <path d="M14.2 8.2l1.8 1.4" />
    <circle cx="12" cy="13.5" r="2.25" />
  </SvgBase>
);

export const TargetIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="12" cy="12" r="1.25" />
    <path d="M12 4V2" />
    <path d="M12 22v-2" />
    <path d="M4 12H2" />
    <path d="M22 12h-2" />
  </SvgBase>
);

export const FlameIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M12 22c4 0 7-3 7-7 0-3-1.7-5.2-3.7-7.2-.7 2-2.1 3.2-3.3 4.1.2-2.2-.6-4.3-2.4-6.1C8 7.8 6 10.3 6 14c0 5 3 8 6 8z" />
    <path d="M10.5 15.2c-.6 2 1 3.8 2.6 3.8 1.6 0 2.9-1.2 2.9-2.8 0-1.3-.8-2.4-1.8-3.4-.2 1-.9 1.6-1.4 2 .1-1.2-.3-2.3-1.2-3.2-1 1.1-1.7 2.4-1.1 3.6z" />
  </SvgBase>
);

export const CrownIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M4 18h16" />
    <path d="M5 18l2-10 5 5 5-5 2 10" />
    <path d="M7.2 8.2l-2-2" />
    <path d="M12 6V4" />
    <path d="M16.8 8.2l2-2" />
  </SvgBase>
);



export const BoltIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M13 2L5 13h6l-1 9 9-12h-6l0-8z" />
  </SvgBase>
);

export const StarBadgeIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path d="M12 2l2.1 2.2 3-.2 1.2 2.8 2.7 1.3-.9 2.9 1.8 2.4-2.4 1.8.2 3-2.8 1.2-1.3 2.7-2.9-.9L12 22l-2.2-2.1-2.9.9-1.3-2.7-2.8-1.2.2-3-2.4-1.8L2.4 13l-.9-2.9L4.2 8.8 5.4 6l3 .2L12 2z" />
    <path d="M12 7.2l1.2 2.6 2.8.4-2 2 .5 2.8-2.5-1.4-2.5 1.4.5-2.8-2-2 2.8-.4L12 7.2z" />
  </SvgBase>
);

/* ---------------------------------------------------------------------------
 * Everything below here kept functionally identical (only tiny typing cleanup)
 * -------------------------------------------------------------------------*/

export const FishingRodIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={2.25}>
    <path d="M4 4l10.5 10.5" />
    <path d="M14.5 14.5l2-2" />
    <path d="M16.5 12.5l2.75-6.25" />
    <path d="M19.25 6.25c.8.8 1.25 1.9 1.25 3.03V18a2.25 2.25 0 01-2.25 2.25h-2.5" />
    <circle cx="18.25" cy="18.25" r="1.25" />
  </SvgBase>
);

export const FishingReelIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM9 7.5a.75.75 0 000 1.5h6a.75.75 0 000-1.5H9zM12 10.5a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75z"
      clipRule="evenodd"
    />
  </svg>
);

export const FishingLureIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.923 2.597a.75.75 0 011.056-.474 8.25 8.25 0 015.897 10.226.75.75 0 01-1.467-.312 6.75 6.75 0 00-4.826-8.37.75.75 0 01-.66-.994z" />
    <path
      fillRule="evenodd"
      d="M18.776 13.97a.75.75 0 01.378 1.007 8.25 8.25 0 01-12.72 2.78l-4.17 3.916a.75.75 0 11-1.028-1.094l4.234-3.977a8.25 8.25 0 014.22-13.376.75.75 0 01.218 1.484 6.75 6.75 0 00-3.454 10.944l3.879-3.642a.75.75 0 011.028 1.094l-3.815 3.582a6.75 6.75 0 0010.223-2.227.75.75 0 011.007-.499z"
      clipRule="evenodd"
    />
  </svg>
);

export const BaitIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a2.25 2.25 0 01-2.25 2.25c-.554 0-1.108.033-1.66.097A6.75 6.75 0 0113.5 15.03v1.845a2.25 2.25 0 01-1.5 2.122V21a.75.75 0 01-1.5 0v-2.003a2.25 2.25 0 01-1.5-2.122v-1.845a6.75 6.75 0 01-5.42-4.039c-.552-.064-1.106-.097-1.66-.097a2.25 2.25 0 01-2.25-2.25C1.777 6.64 6.608 4.296 11.7 2.805z" />
  </svg>
);



export const ChartBarIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M3 6a3 3 0 013-3h2.25a3 3 0 013 3v2.25a3 3 0 01-3 3H6a3 3 0 01-3-3V6zm9.75 0a3 3 0 013-3H18a3 3 0 013 3v2.25a3 3 0 01-3-3h-2.25a3 3 0 01-3-3V6zM3 15.75a3 3 0 013-3h2.25a3 3 0 013 3V18a3 3 0 01-3-3H6a3 3 0 01-3-3v-2.25zm9.75 0a3 3 0 013-3H18a3 3 0 013 3V18a3 3 0 01-3-3h-2.25a3 3 0 01-3-3v-2.25z"
      clipRule="evenodd"
    />
  </svg>
);

export const BookOpenIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.25 4.533A9.707 9.707 0 006 3.75a9.707 9.707 0 00-6 2.25v10.25a9.707 9.707 0 016-2.25c2.478 0 4.7.775 6.502 2.09V4.533zM12.75 4.533c1.801-1.315 4.024-2.09 6.502-2.09 2.22 0 4.226.697 6 1.87V14.5c-1.51-1.08-3.633-1.75-6-1.75-2.22 0-4.226.698-6 1.872V4.533z" />
  </svg>
);

/* Mascot + QR + remaining icons: unchanged from your version */
export const FishMascotIcon: React.FC<IconProps & { variant?: 'default' | 'nav' }> = ({
  className,
  variant = 'default',
}) => (
  <RoddoFishIcon
    className={className || 'h-6 w-6'}
    mode="png"
    animated={false}
    variant={variant === 'nav' ? 'blue' : 'peach'}
  />
);

export const QRCodeIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" shapeRendering="crispEdges">
    <rect x="10" y="10" width="30" height="30" fill="#1e293b" />
    <rect x="15" y="15" width="20" height="20" fill="#f8fafc" />
    <rect x="20" y="20" width="10" height="10" fill="#1e293b" />
    <rect x="60" y="10" width="30" height="30" fill="#1e293b" />
    <rect x="65" y="15" width="20" height="20" fill="#f8fafc" />
    <rect x="70" y="20" width="10" height="10" fill="#1e293b" />
    <rect x="10" y="60" width="30" height="30" fill="#1e293b" />
    <rect x="15" y="65" width="20" height="20" fill="#f8fafc" />
    <rect x="20" y="70" width="10" height="10" fill="#1e293b" />
    <rect x="45" y="10" width="5" height="5" fill="#22d3ee" />
    <rect x="55" y="10" width="5" height="5" fill="#1e293b" />
    <rect x="45" y="15" width="5" height="5" fill="#1e293b" />
    <rect x="45" y="25" width="5" height="5" fill="#22d3ee" />
    <rect x="50" y="30" width="5" height="5" fill="#1e293b" />
    <rect x="45" y="35" width="5" height="5" fill="#1e293b" />
    <rect x="10" y="45" width="5" height="5" fill="#22d3ee" />
    <rect x="10" y="55" width="5" height="5" fill="#1e293b" />
    <rect x="15" y="45" width="5" height="5" fill="#1e293b" />
    <rect x="25" y="45" width="5" height="5" fill="#22d3ee" />
    <rect x="30" y="50" width="5" height="5" fill="#1e293b" />
    <rect x="35" y="45" width="5" height="5" fill="#1e293b" />
    <rect x="60" y="45" width="5" height="5" fill="#22d3ee" />
    <rect x="75" y="50" width="5" height="5" fill="#1e293b" />
    <rect x="65" y="55" width="5" height="5" fill="#1e293b" />
    <rect x="60" y="60" width="5" height="5" fill="#1e293b" />
    <rect x="55" y="65" width="5" height="5" fill="#22d3ee" />
    <rect x="50" y="70" width="5" height="5" fill="#1e293b" />
    <rect x="45" y="75" width="5" height="5" fill="#1e293b" />
    <rect x="55" y="80" width="5" height="5" fill="#22d3ee" />
    <rect x="65" y="85" width="5" height="5" fill="#1e293b" />
    <rect x="75" y="75" width="5" height="5" fill="#1e293b" />
    <rect x="85" y="65" width="5" height="5" fill="#22d3ee" />
  </svg>
);

/* NOTE:
   I didn’t rewrite the remainder to avoid accidentally changing any of your custom paths.
   If you want, paste the tail section and I’ll run the same stroke/solid pass through it too. */

export const FlagIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      fillRule="evenodd"
      d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.158l-.108-.054a8.25 8.25 0 00-5.69-.625l-2.202.55V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z"
      clipRule="evenodd"
      stroke="none"
    />
    <BubbleShine />
  </svg>
);

export const ShareIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth={ICON_STROKE_BOLD}
  >
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7Z" />
  </SvgBase>
);


export const CheckIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

export const XIcon: React.FC<IconProps> = ({ className }) => (
  <SvgBase className={className} fill="none" strokeWidth={ICON_STROKE_BOLD}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </SvgBase>
);

export const CreditCardIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M2.25 8.25a.75.75 0 01.75-.75h18a.75.75 0 01.75.75v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V8.25zM14.25 12a.75.75 0 00-.75.75v1.5c0 .414.336.75.75.75h3a.75.75 0 00.75-.75v-1.5a.75.75 0 00-.75-.75h-3z" />
    <path d="M2.25 5.25a3 3 0 013-3h13.5a3 3 0 013 3V6.75H2.25V5.25z" />
  </svg>
);

export const PayPalIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M7.754 6.36a.533.533 0 0 0-.523.533v10.15c0 .034.002.066.004.098.05.474.153.86.302 1.15.207.395.492.684.856.865.363.18.79.27 1.28.27h.13c3.484 0 6.006-2.043 6.44-5.592.03-.25.045-.503.045-.758 0-.25-.015-.5-.045-.742-.435-3.51-2.924-5.5-6.32-5.5h-2.17a.533.533 0 0 0-.522.515zm1.538.533h1.12c2.45 0 4.14 1.455 4.502 4.16.03.22.046.438.046.657 0 .22-.016.44-.047.66-.363 2.72-2.052 4.195-4.5 4.195h-.06c-.46 0-.85-.09-1.17-.27-.32-.18-.56-.45-.72-.81-.16-.36-.26-.82-.3-1.38v-6.852zM4.194 8.718c-.12.27-.203.58-.25 1.025-.047.445-.07.864-.07 1.258 0 .42.023.87.07 1.34.047.47.13.88.25 1.22.12.34.28.61.47.81.19.2.42.33.68.39h.03c.09 0 .15-.06.15-.15v-.06c0-.09-.06-.15-.15-.15-.18-.04-.32-.14-.42-.29-.1-.15-.17-.36-.22-.61-.09-.53-.14-1.1-.14-1.7s.05-1.17.14-1.7c.05-.25.12-.46.22-.61.1-.15.24-.25.42-.29.09-.02.15-.08.15-.17s-.06-.15-.15-.15h-.03c-.26.06-.49.19-.68.39-.19.2-.35.47-.47.81z" />
  </svg>
);

export const ApplePayIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M42.5 32.8a9.4 9.4 0 0 1-3.1-7.4c0-5.3 4.1-9.3 9.4-9.3a1.4 1.4 0 0 0-1-1.3c-3.1-.9-6.3 1-8.2 3.6a11.1 11.1 0 0 0-4.6 9 10.3 10.3 0 0 0 4.2 8.7 1.3 1.3 0 0 0 1.5-1.2 1.6 1.6 0 0 0-.2-2.1zm-8.2-19.4c-5.7-6.2-15-6.4-21.2-.5a15.2 15.2 0 0 0-7 12.8c0 7.8 5.4 15.9 12.8 15.9 2.5 0 5.3-1.4 7.9-1.4s4.8 1.4 7.9 1.4c7.6 0 11.9-7.2 12-15.3a18.2 18.2 0 0 0-1.4-7.2c-2.4-3.7-6-5.8-11-5.7z" />
  </svg>
);

export const CheckCircleIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

export const LockIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
      clipRule="evenodd"
    />
  </svg>
);

export const MarketplaceIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 9.25V20h16V9.25" />
    <path d="M3 9.25h18L19.25 4H4.75L3 9.25Z" />
    <path d="M3 9.25c0 1.35 1.02 2.45 2.28 2.45S7.55 10.6 7.55 9.25c0 1.35 1.02 2.45 2.27 2.45s2.28-1.1 2.28-2.45c0 1.35 1.02 2.45 2.27 2.45s2.28-1.1 2.28-2.45c0 1.35 1.02 2.45 2.27 2.45S21 10.6 21 9.25" />
    <path d="M7.25 20v-5.25h4.5V20" />
    <path d="M15 14.75h2.75" />
  </svg>
);

export const StateSealIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1s-1 .45-1 1v3h-1c-1.1 0-2 .9-2 2v1H6.5c.83 2.32 3.02 4 5.5 4s4.67-1.68 5.5-4h-1.8zM12 4.5c1.82 0 3.42.87 4.47 2.22L15 8.25c-.47-.23-1-.35-1.5-.45V6c0-.55-.45-1-1-1s-1 .45-1 1v1.8c-.5.1-1.03.22-1.5.45L8.53 6.72C9.58 5.37 11.18 4.5 12 4.5z" />
  </svg>
);

export const HologramIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <defs>
      <linearGradient id="holoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#A491D3', stopOpacity: 0.8 }} />
        <stop offset="50%" style={{ stopColor: '#A2D2BA', stopOpacity: 0.8 }} />
        <stop offset="100%" style={{ stopColor: '#FFF1A7', stopOpacity: 0.8 }} />
      </linearGradient>
    </defs>
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="url(#holoGradient)" stroke="none" />
    <path d="M2 17l10 5 10-5" stroke="url(#holoGradient)" />
    <path d="M2 12l10 5 10-5" stroke="url(#holoGradient)" />
  </svg>
);

export const HeartIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
  </svg>
);

export const MegaphoneIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M16.881 4.345A23.112 23.112 0 018.25 6H7.5a5.25 5.25 0 00-.88 10.427 21.593 21.593 0 001.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 01-.628-1.6.75.75 0 011.452-.37c.349.898.74 1.769 1.16 2.593.14.274.41.447.716.458.307.01.605-.134.772-.379l.853-1.251c.31-.455.275-1.058-.094-1.466a23.948 23.948 0 00-2.338-2.208 2.25 2.25 0 00-.832-1.635l-.234-.177c-1.825-1.383-3.814-2.56-5.898-3.477.535-.23.992-.625 1.253-1.127l.003-.005a2.25 2.25 0 00-.317-2.673l-.005-.005a2.25 2.25 0 00-2.677-.312l-.004.002zM18 6a.75.75 0 100 1.5.75.75 0 000-1.5zM18 9.75a.75.75 0 100 1.5.75.75 0 000-1.5zM19.5 7.875a.75.75 0 100 1.5.75.75 0 000-1.5z" />
  </svg>
);

export const ScanIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      fillRule="evenodd"
      d="M12 4.5c2.55 0 5.028.72 7.095 2.137.28.192.524.426.713.702a.75.75 0 01-.132 1.011 4.75 4.75 0 00-1.677 3.65c0 1.439.636 2.72 1.677 3.65a.75.75 0 01.132 1.01 13.902 13.902 0 01-7.096 2.138c-4.468 0-8.26-2.943-9.542-7a13.926 13.926 0 019.542-7.397z"
      clipRule="evenodd"
    />
  </svg>
);

export const DroneIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z" />
    <path
      fillRule="evenodd"
      d="M20.94 11c-.46-4.17-3.77-7.48-7.94-7.94a.75.75 0 01.15-1.49 9.49 9.49 0 019.28 9.28.75.75 0 01-1.49.15z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M3.06 13c.46 4.17 3.77 7.48 7.94 7.94a.75.75 0 01-.15 1.49 9.49 9.49 0 01-9.28-9.28.75.75 0 011.49-.15z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M11 3.06C6.83 3.52 3.52 6.83 3.06 11a.75.75 0 01-1.49-.15A9.49 9.49 0 0110.85 1.57a.75.75 0 01.15 1.49z"
      clipRule="evenodd"
    />
    <path
      fillRule="evenodd"
      d="M13 20.94c4.17-.46 7.48-3.77 7.94-7.94a.75.75 0 011.49.15 9.49 9.49 0 01-9.28 9.28.75.75 0 01-.15-1.49z"
      clipRule="evenodd"
    />
  </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const ChevronUpIcon: React.FC<IconProps> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 15l6-6 6 6" />
  </svg>
);

export const DownloadCloudIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-.53 14.03a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V8.25a.75.75 0 00-1.5 0v5.69l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3z"
      clipRule="evenodd"
    />
  </svg>
);

export const SparklesIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path
      fillRule="evenodd"
      d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813a3.75 3.75 0 002.576-2.576l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5zM16.5 15a.75.75 0 01.712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 010 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 01-1.422 0l-.395-1.183a1.5 1.5 0 00-.948-.948l-1.183-.395a.75.75 0 010-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0116.5 15z"
      clipRule="evenodd"
      stroke="none"
    />
    <BubbleShine />
  </svg>
);

export const MapPinIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M11.54 22.351l.07.04.028.016a.75.75 0 00.724 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
      clipRule="evenodd"
    />
  </svg>
);

export const CrosshairIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V12a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V6zM12 15a.75.75 0 00-.75.75v2.25a.75.75 0 001.5 0v-2.25A.75.75 0 0012 15z"
      clipRule="evenodd"
    />
  </svg>
);

export const GlobeIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM8.547 4.505a8.25 8.25 0 0110.172 10.172 8.25 8.25 0 01-10.172-10.172zM4.505 8.546a8.25 8.25 0 0110.172 10.172 8.25 8.25 0 01-10.172-10.172z"
      clipRule="evenodd"
    />
    <path d="M12 5.25a.75.75 0 01.75.75v12a.75.75 0 01-1.5 0V6a.75.75 0 01.75-.75z" />
    <path d="M5.25 12a.75.75 0 01.75-.75h12a.75.75 0 010 1.5H6a.75.75 0 01-.75-.75z" />
  </svg>
);

export const FishIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M15 9.5c3.5 0 6 2.5 6 2.5s-2.5 2.5-6 2.5" />
    <path d="M15 9.5C10 8 6 12 6 12s4 4 9 2.5" />
    <path d="M10.5 10.5c.5 1 .5 2 0 3" />
    <path d="M8.5 12h.01" />
  </svg>
);

export const PhotoIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
      clipRule="evenodd"
    />
  </svg>
);

export const LogoutIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

export const RoddoLogo: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 80"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <filter id="roddo-burn-emboss" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" result="blur" />
        <feOffset in="blur" dx="0.7" dy="0.9" result="shadow" />
        <feFlood floodColor="#241711" floodOpacity="0.68" result="shadowColor" />
        <feComposite in="shadowColor" in2="shadow" operator="in" result="darkShadow" />

        <feOffset in="SourceAlpha" dx="-0.45" dy="-0.45" result="highlightOffset" />
        <feComposite in="highlightOffset" in2="SourceAlpha" operator="out" result="highlightMask" />
        <feFlood floodColor="#f0c997" floodOpacity="0.22" result="highlightColor" />
        <feComposite in="highlightColor" in2="highlightMask" operator="in" result="lightEdge" />

        <feMerge>
          <feMergeNode in="darkShadow" />
          <feMergeNode in="lightEdge" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      <filter id="roddo-burn-emboss-soft" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.35" result="blur" />
        <feOffset in="blur" dx="0.45" dy="0.6" result="shadow" />
        <feFlood floodColor="#2a1b14" floodOpacity="0.45" result="shadowColor" />
        <feComposite in="shadowColor" in2="shadow" operator="in" result="darkShadow" />

        <feOffset in="SourceAlpha" dx="-0.3" dy="-0.3" result="highlightOffset" />
        <feComposite in="highlightOffset" in2="SourceAlpha" operator="out" result="highlightMask" />
        <feFlood floodColor="#f4d5aa" floodOpacity="0.16" result="highlightColor" />
        <feComposite in="highlightColor" in2="highlightMask" operator="in" result="lightEdge" />

        <feMerge>
          <feMergeNode in="darkShadow" />
          <feMergeNode in="lightEdge" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Post */}
    <rect
      x="90"
      y="0"
      width="20"
      height="80"
      fill="#4a3728"
      stroke="#2e2118"
      strokeWidth="2"
      rx="2"
    />

    {/* Board shadow */}
    <path
      d="M15 15 H185 L182 65 H12 Z"
      fill="rgba(0,0,0,0.25)"
      transform="translate(3, 3)"
    />

    {/* Main wooden board */}
    <path
      d="M10 15 L190 15 L188 25 L192 35 L188 45 L190 55 L185 65 H15 L12 55 L10 45 L14 35 L10 25 Z"
      fill="#d4a373"
      stroke="#6d4c41"
      strokeWidth="2"
    />

    {/* Wood grain */}
    <path
      d="M20 25 Q 60 22, 100 25 T 180 23"
      fill="none"
      stroke="#a17a5b"
      strokeWidth="1.5"
    />
    <path
      d="M20 55 Q 60 58, 100 55 T 180 57"
      fill="none"
      stroke="#a17a5b"
      strokeWidth="1.5"
    />
    <path
      d="M50 40 Q 70 35, 90 40 T 130 40"
      fill="none"
      stroke="#a17a5b"
      strokeWidth="1.5"
      opacity="0.6"
    />

    {/* Cracks */}
    <path
      d="M40 15 L45 30 L43 45"
      fill="none"
      stroke="#3e2723"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M160 65 L155 50 L158 40"
      fill="none"
      stroke="#3e2723"
      strokeWidth="1.5"
      strokeLinecap="round"
    />

    {/* Fishing rod moved left */}
    <g transform="translate(30, 38) rotate(-12)">
      <path
        d="M0 20 Q 5 -25, 30 -20"
        fill="none"
        stroke="#3e2723"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="5" cy="15" r="3" fill="#3e2723" />
      <path
        d="M30 -20 L 30 0"
        fill="none"
        stroke="#3e2723"
        strokeWidth="1"
      />
      <path
        d="M30 0 Q 26 5, 30 8"
        fill="none"
        stroke="#3e2723"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </g>

    {/* Main text with burn emboss */}
    <text
      x="100"
      y="36"
      fontFamily="'Gaegu', cursive"
      fontWeight="700"
      fontSize="30"
      fill="#3a241a"
      textAnchor="middle"
      filter="url(#roddo-burn-emboss)"
      style={{ letterSpacing: '0.2px' }}
    >
      Roddo
    </text>

    {/* Tagline with softer emboss */}
    <text
      x="100"
      y="52"
      fontFamily="'Gaegu', cursive"
      fontWeight="700"
      fontSize="9"
      fill="#3e2723"
      textAnchor="middle"
      filter="url(#roddo-burn-emboss-soft)"
      style={{ letterSpacing: '0.25px' }}
    >
      Your Fishing Pocket Buddy
    </text>

    {/* Nails */}
    <circle
      cx="100"
      cy="22"
      r="3"
      fill="#90a4ae"
      stroke="#546e7a"
      strokeWidth="1"
    />
    <circle
      cx="100"
      cy="52"
      r="3"
      fill="#90a4ae"
      stroke="#546e7a"
      strokeWidth="1"
    />
  </svg>
);

/* ---------------------------------------------------------------------------
 * Legacy / extra icons to keep older imports working
 * -------------------------------------------------------------------------*/

// FishIQ (AI Knowledge)
export const FishIQIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

export const BrainIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 6.5c-1.5-3-6-2.5-6 2 0 2 .5 3 1.5 4M12 6.5c1.5-3 6-2.5 6 2 0 2-.5 3-1.5 4M6.5 12.5c-2.5 1-2.5 5 1 5 .5 2 3 3 4.5 1.5M17.5 12.5c2.5 1 2.5 5-1 5-.5 2-3 3-4.5 1.5" />
  </svg>
);

// Bazaar / Marketplace
export const BazaarIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9h18" />
    <path d="M3 9l2-5h14l2 5" />
    <path d="M5 9v10h14V9" />
    <path d="M10 13h4" />
  </svg>
);

// Tackle Box (Bottom nav)
export const TackleBoxIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7h18v4H3z" />
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M9 7V5a3 3 0 0 1 6 0v2" />
  </svg>
);

// Duel rods icon
export const DuelRodsIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 22 L10 3" strokeLinecap="round" />
    <path d="M21 22 L14 3" strokeLinecap="round" />
    <path d="M8 8 L16 8" strokeLinecap="round" />
  </svg>
);

// Settings alias (for older imports)
export const SettingsIcon: React.FC<IconProps> = ({ className }) => (
  <CogIcon className={className} />
);

// Alert triangle
export const AlertTriangleIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.29 3.86L1.82 18a1 1 0 00.86 1.5h18.64a1 1 0 00.86-1.5L13.71 3.86a1 1 0 00-1.72 0z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 3h.01" />
  </svg>
);

// Spinner
export const SpinnerIcon: React.FC = () => (
  <svg className="animate-spin h-5 w-5 text-current" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"
    />
  </svg>
);

// Timeline / logistics icons
export const TruckIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h11v8H3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 9h3l3 3v3h-6z" />
    <circle cx="7" cy="17" r="2" />
    <circle cx="17" cy="17" r="2" />
  </svg>
);

export const BoxIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4-9 4-9-4z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10l9 4 9-4V7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v10" />
  </svg>
);

export const ClockIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 21a9 9 0 100-18 9 9 0 000 18z"
    />
  </svg>
);

export const PackageIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4-9 4-9-4z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7v6l-9 4-9-4V7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v6" />
  </svg>
);

export const CalendarIcon: React.FC<IconProps> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="3" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
